import { ReactElement, useState } from 'react'
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { MaterialSymbol } from '@ui2/icons'

interface Props {
  /** Available assembly labels (the rows' `qs` values). */
  options: string[]
  /** Selected assembly labels; empty means all. */
  selected: string[]
  onChange: (next: string[]) => void
}

/**
 * Filter button for the assembly lists (Wände / Decken / Dächer) — a boxed icon button (matching the
 * element list's ListFilterButton) opening a popover with a multi-select of the available assemblies.
 */
const AssemblyFilterButton = ({ options, selected, onChange }: Props): ReactElement => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const isActive = selected.length > 0

  return (
    <>
      <Tooltip title="Filter" arrow disableInteractive>
        <Badge color="primary" variant="dot" invisible={!isActive} overlap="circular">
          <Box
            component="button"
            type="button"
            data-cy="results2-assembly-filter-button"
            onClick={e => setAnchorEl(e.currentTarget)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              cursor: 'pointer',
              flexShrink: 0,
              background: isActive
                ? alpha(theme.palette.primary.main, 0.1)
                : theme.palette.common.white,
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:hover': { background: theme.palette.action.hover },
            }}
          >
            <MaterialSymbol icon="filter_alt" size={18} filled={isActive} title="Filter" />
          </Box>
        </Badge>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 288, p: 1.5 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Filter</Typography>
            <Button
              size="small"
              onClick={() => {
                onChange([])
                setAnchorEl(null)
              }}
              disabled={!isActive}
              data-cy="results2-assembly-filter-reset"
              sx={{ fontSize: 11, minWidth: 0, px: 0.75, py: 0.25, textTransform: 'none' }}
            >
              Zurücksetzen
            </Button>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 500, mb: 0.5, color: 'text.secondary' }}>
              Aufbau
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={options}
              value={selected}
              onChange={(_, next) => onChange(next)}
              ChipProps={{ size: 'small' }}
              noOptionsText="Keine Aufbauten"
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={selected.length === 0 ? 'Alle' : ''}
                  inputProps={{
                    ...params.inputProps,
                    'data-cy': 'results2-assembly-filter-input',
                  }}
                />
              )}
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default AssemblyFilterButton
